import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  CalendarAuth,
  CalendarEngine,
  parseCalendarCallbackParams,
  type CalendarRefreshResult,
} from '@trackmind/sync'
import { CalendarCache } from '@trackmind/storage'
import { useApp } from '@/context/AppContext'
import type {
  CalendarAccount,
  CalendarEvent,
  CalendarPrivacy,
  CalendarSource,
  CalendarSourceUpdate,
} from '@trackmind/core'
import { eventsToBusySlots, type BusySlot } from '@trackmind/core'

/**
 * Calendar state lives in its own context, separate from AppContext.
 *
 * Not just for file size: calendars are deliberately NOT part of the app's
 * synced data. Keeping them out of AppContext keeps them out of the state
 * that gets exported, merged and pushed to the cloud — the separation is
 * structural rather than a rule someone has to remember.
 */

interface CalendarContextValue {
  /** Null when VITE_GOOGLE_CLIENT_ID isn't configured for this deployment. */
  available: boolean
  accounts: CalendarAccount[]
  sources: CalendarSource[]
  events: CalendarEvent[]
  loading: boolean
  refreshing: boolean
  lastRefreshedAt: string | null
  error: string | null
  /** Accounts whose grant died — the UI prompts to reconnect these. */
  needsReauth: CalendarAccount[]

  /**
   * `email` pre-selects a Google account the user is already signed in to, so
   * connecting its calendar is one approval rather than another sign-in.
   */
  connectAccount: (options?: { email?: string }) => Promise<void>
  disconnectAccount: (accountId: string) => Promise<void>
  /** Opt in/out of publishing one account to the user's other devices. */
  setAccountSharing: (
    accountId: string,
    share: boolean,
    privacy?: CalendarPrivacy,
  ) => Promise<void>
  disconnectAll: () => Promise<void>
  updateSource: (sourceId: string, update: CalendarSourceUpdate) => Promise<void>
  refresh: (options?: { force?: boolean }) => Promise<CalendarRefreshResult | null>

  /** Events touching one local day, ordered by start time. */
  eventsForDate: (date: string) => CalendarEvent[]
  /** Busy slots for the scheduler — calendar names only, never event titles. */
  busySlotsForDate: (date: string) => BusySlot[]
  hasBlockingCalendar: boolean
}

/** A short, human label so "last updated by" on a mirror means something. */
function describeDevice(): string {
  const ua = navigator.userAgent
  if (/iPhone|iPad|iPod/.test(ua)) return 'iPhone/iPad'
  if (/Android/.test(ua)) return 'Android'
  if (/Macintosh/.test(ua)) return 'Mac'
  if (/Windows/.test(ua)) return 'Windows PC'
  if (/Linux/.test(ua)) return 'Linux'
  return 'another device'
}

const CalendarContext = createContext<CalendarContextValue | null>(null)

export function useCalendar(): CalendarContextValue {
  const ctx = useContext(CalendarContext)
  if (!ctx) throw new Error('useCalendar must be used within CalendarProvider')
  return ctx
}

export function CalendarProvider({ children }: { children: React.ReactNode }) {
  // Cross-device mirroring rides on the same raw-file API finance uses, so it
  // is provider-agnostic: GitHub and Drive users get identical behaviour.
  const { readRawFile, writeRawFile, isGitHubConnected, isGoogleConnected } = useApp()
  const isSignedIn = isGitHubConnected || isGoogleConnected
  const [engine, setEngine] = useState<CalendarEngine | null>(null)
  const [accounts, setAccounts] = useState<CalendarAccount[]>([])
  const [sources, setSources] = useState<CalendarSource[]>([])
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const available = Boolean(import.meta.env.VITE_GOOGLE_CLIENT_ID)
  const engineRef = useRef<CalendarEngine | null>(null)

  const reloadFromCache = useCallback(async (e: CalendarEngine) => {
    const [nextAccounts, nextSources, nextEvents] = await Promise.all([
      e.getAccounts(),
      e.getSources(),
      e.getEventsInRange('0000-01-01', '9999-12-31'),
    ])
    setAccounts(nextAccounts)
    setSources(nextSources)
    setEvents(nextEvents)
  }, [])

  // ── Boot: open the cache, finish any pending connect, first refresh ─────
  useEffect(() => {
    let cancelled = false

    async function init() {
      if (!available) {
        setLoading(false)
        return
      }

      try {
        const cache = new CalendarCache()
        await cache.initialize()
        const auth = CalendarAuth.createFromEnv()
        const instance = new CalendarEngine({
          cache,
          auth,
          deviceLabel: describeDevice(),
        })
        if (cancelled) return
        engineRef.current = instance
        setEngine(instance)

        // A calendar-connect redirect lands here. AppContext's sign-in
        // handlers deliberately ignore `cal.`-prefixed states so this one
        // isn't consumed by them.
        const callback = parseCalendarCallbackParams(window.location.href)
        if (callback) {
          try {
            const user = await auth.handleCallback(callback.code, callback.state)
            await instance.registerAccount(user)
          } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to connect calendar')
          } finally {
            window.history.replaceState({}, '', window.location.pathname)
          }
        }

        await reloadFromCache(instance)
        setLoading(false)

        // Show cached events immediately, then poll in the background —
        // opening the app must never wait on somebody else's API.
        //
        // This runs even with no accounts connected here: a device that has
        // never signed in to any calendar can still be handed mirrored ones
        // from the user's other devices.
        instance.refresh({ force: Boolean(callback) })
          .then(async (result) => {
            if (cancelled) return
            await reloadFromCache(instance)
            setLastRefreshedAt(new Date().toISOString())
            if (result.failedAccounts.length > 0) {
              setError(result.failedAccounts[0].error)
            }
          })
          .catch((e) => console.error('[Calendar] initial refresh failed:', e))
        instance.startAutoRefresh()
      } catch (e) {
        console.error('[Calendar] init failed:', e)
        setError(e instanceof Error ? e.message : 'Calendar unavailable')
        setLoading(false)
      }
    }

    init()
    return () => {
      cancelled = true
      engineRef.current?.stopAutoRefresh()
    }
  }, [available, reloadFromCache])

  // Sign-in can happen long after the engine boots (and can go away again),
  // so the transport is attached and detached rather than passed once.
  useEffect(() => {
    if (!engine) return
    engine.setRemote(isSignedIn ? { readRawFile, writeRawFile } : null)
  }, [engine, isSignedIn, readRawFile, writeRawFile])

  // ── Poll when the user comes back to the app ────────────────────────────
  // Same trigger the data sync uses. This is what makes a meeting added on
  // your phone show up here without you asking: there is no push channel
  // without a server, so "coming back to the app" is the moment to check.
  useEffect(() => {
    if (!engine) return
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      engine
        .refresh()
        .then(async (result) => {
          if (result.refreshedSources === 0) return
          await reloadFromCache(engine)
          setLastRefreshedAt(new Date().toISOString())
        })
        .catch((e) => console.error('[Calendar] visibility refresh failed:', e))
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [engine, reloadFromCache])

  const refresh = useCallback(
    async (options: { force?: boolean } = {}) => {
      if (!engine) return null
      setRefreshing(true)
      setError(null)
      try {
        const result = await engine.refresh({ force: options.force ?? true })
        await reloadFromCache(engine)
        setLastRefreshedAt(new Date().toISOString())
        if (result.failedAccounts.length > 0) setError(result.failedAccounts[0].error)
        return result
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Calendar refresh failed')
        return null
      } finally {
        setRefreshing(false)
      }
    },
    [engine, reloadFromCache],
  )

  const connectAccount = useCallback(async (options: { email?: string } = {}) => {
    if (!available) throw new Error('Calendar sign-in is not configured for this deployment')
    await CalendarAuth.createFromEnv().startFlow(options)
  }, [available])

  const disconnectAccount = useCallback(
    async (accountId: string) => {
      if (!engine) return
      await engine.disconnectAccount(accountId)
      await reloadFromCache(engine)
    },
    [engine, reloadFromCache],
  )

  const setAccountSharing = useCallback(
    async (accountId: string, share: boolean, privacy?: CalendarPrivacy) => {
      if (!engine) return
      setRefreshing(true)
      try {
        await engine.setAccountSharing(accountId, share, privacy)
        await reloadFromCache(engine)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to update sharing')
      } finally {
        setRefreshing(false)
      }
    },
    [engine, reloadFromCache],
  )

  const disconnectAll = useCallback(async () => {
    if (!engine) return
    await engine.disconnectAll()
    engine.stopAutoRefresh()
    await reloadFromCache(engine)
  }, [engine, reloadFromCache])

  const updateSource = useCallback(
    async (sourceId: string, update: CalendarSourceUpdate) => {
      if (!engine) return
      setRefreshing(true)
      try {
        await engine.updateSource(sourceId, update)
        await reloadFromCache(engine)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to update calendar')
      } finally {
        setRefreshing(false)
      }
    },
    [engine, reloadFromCache],
  )

  // ── Derived reads ───────────────────────────────────────────────────────

  const enabledSourceIds = useMemo(
    () => new Set(sources.filter((s) => s.enabled).map((s) => s.id)),
    [sources],
  )

  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>()
    for (const event of events) {
      // A calendar switched off between refreshes must disappear at once.
      if (!enabledSourceIds.has(event.source_id)) continue
      const list = map.get(event.date)
      if (list) list.push(event)
      else map.set(event.date, [event])
    }
    for (const list of map.values()) {
      list.sort((a, b) => {
        if (a.all_day !== b.all_day) return a.all_day ? -1 : 1
        return a.start < b.start ? -1 : a.start > b.start ? 1 : 0
      })
    }
    return map
  }, [events, enabledSourceIds])

  const sourcesById = useMemo(() => new Map(sources.map((s) => [s.id, s])), [sources])

  const eventsForDate = useCallback(
    (date: string) => eventsByDate.get(date) ?? [],
    [eventsByDate],
  )

  const busySlotsForDate = useCallback(
    (date: string) => eventsToBusySlots(eventsByDate.get(date) ?? [], sourcesById, date),
    [eventsByDate, sourcesById],
  )

  const needsReauth = useMemo(() => accounts.filter((a) => a.needs_reauth), [accounts])

  const hasBlockingCalendar = useMemo(
    () => sources.some((s) => s.enabled && s.blocks_time),
    [sources],
  )

  const value: CalendarContextValue = {
    available,
    accounts,
    sources,
    events,
    loading,
    refreshing,
    lastRefreshedAt,
    error,
    needsReauth,
    connectAccount,
    disconnectAccount,
    setAccountSharing,
    disconnectAll,
    updateSource,
    refresh,
    eventsForDate,
    busySlotsForDate,
    hasBlockingCalendar,
  }

  return <CalendarContext.Provider value={value}>{children}</CalendarContext.Provider>
}
